// Blog RAMA DÉCO — guides & conseils. Contenu rédigé, orienté méthode (aucune fourchette de prix affichée).

export const GUIDES = [
  {
    slug: "renover-appartement-paris-contraintes",
    title: "Rénover un appartement à Paris : les contraintes à anticiper | RAMA DÉCO",
    description:
      "Copropriété, accès, évacuation des gravats, horaires : tout ce qu'il faut anticiper avant de rénover un appartement à Paris, expliqué par une entreprise de second œuvre.",
    h1: "Rénover un appartement à Paris : les contraintes à anticiper",
    kicker: "Méthode · Paris",
    date: "2026-05-14",
    readTime: "8 min",
    zone: "paris",
    img: "https://images.unsplash.com/photo-1680786661510-70f05296870a?auto=format&fit=crop&w=1600&q=80",
    chapo:
      "À Paris, ce n'est presque jamais le logement qui complique le chantier : c'est l'immeuble. Escalier étroit, absence d'ascenseur, règlement de copropriété, stationnement introuvable. Anticiper ces cinq points change la durée d'un chantier — et la tranquillité de tout le monde.",
    sections: [
      {
        h2: "1. Le règlement de copropriété, avant tout le reste",
        p: [
          "C'est le document qui fixe les règles du jeu : horaires autorisés, jours travaillés, protection obligatoire des parties communes, information des voisins, parfois interdiction totale de travaux bruyants pendant certaines périodes.",
          "Nous le lisons systématiquement avant d'établir un planning. Un chantier annoncé sur trois semaines mais limité à quatre heures de travaux bruyants par jour n'est pas le même chantier.",
        ],
        list: [
          "Horaires habituels : 8h-19h en semaine, souvent restreint le samedi, interdit le dimanche.",
          "Déclaration au syndic avec la nature des travaux et les attestations d'assurance.",
          "Protection des sols, murs et ascenseur des parties communes.",
          "Information des occupants par affichage, avant le démarrage.",
        ],
      },
      {
        h2: "2. Ce qui n'est pas modifiable dans un appartement",
        p: [
          "Certaines envies se heurtent à la structure ou à la copropriété. Mieux vaut le savoir avant de rêver sur un plan.",
        ],
        list: [
          "Les murs porteurs : leur modification relève du gros œuvre, avec bureau d'études et accord de l'assemblée. RAMA DÉCO n'intervient pas sur ce périmètre.",
          "Les colonnes d'eau et les chutes d'évacuation communes : on peut s'y raccorder, pas les déplacer.",
          "Les fenêtres et garde-corps, qui appartiennent souvent à l'aspect extérieur de l'immeuble.",
          "Les cloisons non porteuses, en revanche, se déposent et se recréent librement : c'est là que se joue la redistribution.",
        ],
      },
      {
        h2: "3. L'accès : le vrai facteur de durée",
        p: [
          "Un cinquième étage sans ascenseur, c'est chaque sac de plâtre monté à la main et chaque seau de gravats descendu de la même façon. Cela ne rend pas le chantier impossible, cela le rend plus long — et cela doit être dit avant, pas découvert en cours de route.",
        ],
        list: [
          "Absence d'ascenseur : prévoir du temps de portage à chaque phase.",
          "Escalier étroit ou en colimaçon : les grandes plaques et les meubles hauts peuvent ne pas passer.",
          "Stationnement : réservation d'emplacement auprès de la Ville, ou portage sur plusieurs dizaines de mètres.",
          "Évacuation : quand la benne est impossible, la rotation de véhicules devient la règle.",
        ],
      },
      {
        h2: "4. Les particularités du bâti parisien",
        p: [
          "Dans l'ancien haussmannien, la matière est différente : plâtre traditionnel, parquet clouté sur lambourdes, corniches et moulures. Les traiter demande des gestes que le neuf ne demande pas.",
        ],
        list: [
          "Plâtre traditionnel : rebouchage et enduits compatibles, jamais de recouvrement brutal.",
          "Parquet ancien : ponçage et vitrification ou remise en cire, plutôt que remplacement systématique.",
          "Moulures et corniches : dépoussiérage, reprise localisée, peinture à la brosse dans les profils.",
          "Planchers bois : contrôle de la planéité avant toute pose de sol dur.",
        ],
      },
      {
        h2: "5. L'ordre des travaux, celui qui fait gagner du temps",
        p: [
          "Respecter l'enchaînement technique évite de payer deux fois la même finition. C'est la règle la plus rentable d'un chantier, à Paris comme ailleurs.",
        ],
        list: [
          "Dépose des éléments non structurels et évacuation.",
          "Plomberie et électricité : tout ce qui traverse les murs et les sols.",
          "Cloisons, doublages et plafonds, puis bandes et enduits.",
          "Ragréage, puis pose des sols durs.",
          "Faïence et carrelage des pièces d'eau, étanchéité comprise.",
          "Peinture, puis sols souples ou parquets flottants, puis plinthes.",
          "Sanitaires, appareillage électrique, portes et finitions.",
        ],
        p2: [
          "Nous fournissons ce planning daté, poste par poste, avant le démarrage. Il est aussi consultable en ligne dans votre espace projet pendant toute la durée du chantier.",
        ],
      },
    ],
    faq: [
      {
        q: "Faut-il une autorisation pour rénover un appartement à Paris ?",
        a: "Pour des travaux intérieurs sans modification de façade ni de structure, aucune autorisation d'urbanisme n'est requise. Une déclaration auprès du syndic reste nécessaire, et le règlement de copropriété peut imposer des horaires et des protections.",
      },
      {
        q: "Combien de temps dure la rénovation d'un appartement parisien ?",
        a: "Comptez en moyenne 6 à 8 semaines pour un T2 rénové intégralement, 10 à 14 semaines pour un T4 avec deux pièces d'eau. Les contraintes d'accès, les horaires de copropriété et les temps de séchage expliquent l'essentiel de la durée.",
      },
      {
        q: "Le devis peut-il évoluer en cours de chantier ?",
        a: "Uniquement par avenant écrit et validé par vous. Les découvertes après dépose — support dégradé, réseau non conforme — sont chiffrées séparément et soumises à votre accord avant toute exécution.",
      },
    ],
  },
  {
    slug: "renover-maison-seine-saint-denis-par-ou-commencer",
    title: "Rénover une maison en Seine-Saint-Denis : par où commencer | RAMA DÉCO",
    description:
      "Meulière, pavillon des années 60, maison récente : comment aborder la rénovation intérieure d'une maison en Seine-Saint-Denis, dans le bon ordre et sans mauvaise surprise.",
    h1: "Rénover une maison en Seine-Saint-Denis : par où commencer",
    kicker: "Méthode · Seine-Saint-Denis",
    date: "2026-05-02",
    readTime: "7 min",
    zone: "seine-saint-denis",
    img: "https://images.unsplash.com/photo-1741764014072-68953e93cd48?auto=format&fit=crop&w=1600&q=80",
    chapo:
      "Un pavillon ne se rénove pas comme un appartement : on peut presque tout ouvrir, mais on découvre aussi beaucoup plus de choses. Voici la méthode que nous appliquons sur nos chantiers du 93, du diagnostic à la réception.",
    sections: [
      {
        h2: "Commencer par le diagnostic, pas par la déco",
        p: [
          "Avant de choisir une teinte, il faut savoir ce que cache le logement. Notre visite technique suit toujours le même ordre : eau, électricité, supports, sols, ventilation.",
        ],
        list: [
          "Traces d'humidité : origine, ancienneté, extension réelle sous les revêtements.",
          "Tableau électrique : protections, terre, nombre de circuits disponibles.",
          "Planéité des murs et des planchers, état des plâtres et des doublages.",
          "Nature des sols existants et nombre de couches superposées.",
          "Ventilation : présence, fonctionnement, sections d'entrée d'air.",
        ],
      },
      {
        h2: "Ce que change l'âge de la maison",
        p: [
          "Chaque génération de construction a ses points de vigilance. Les reconnaître permet d'anticiper au lieu de subir.",
        ],
        list: [
          "Avant 1948 (meulières) : plâtres épais à reprendre, planchers bois à stabiliser, électricité entièrement à refaire.",
          "1950-1975 : sols multicouches collés, cloisons en carreaux de plâtre, pièces d'eau à démolir intégralement.",
          "1975-2000 : doublages insuffisants, tableaux sous-dimensionnés, cuisines et salles de bains datées.",
          "Après 2000 : travaux surtout esthétiques, parfois une redistribution de cloisons légères.",
        ],
      },
      {
        h2: "L'ordre des travaux, poste par poste",
        p: [
          "Cet enchaînement n'est pas négociable : chaque étape prépare la suivante et protège la finition.",
        ],
        list: [
          "Dépose et évacuation des éléments non structurels.",
          "Plomberie et électricité, avant toute fermeture de mur.",
          "Cloisons, doublages, plafonds, puis bandes et enduits.",
          "Chape ou ragréage, puis pose des sols durs.",
          "Carrelage et faïence des pièces d'eau, avec étanchéité.",
          "Peinture, sols souples, plinthes.",
          "Sanitaires, appareillage, portes, finitions et nettoyage de livraison.",
        ],
      },
      {
        h2: "Rénover en habitant sur place, c'est possible",
        p: [
          "La majorité de nos clients du 93 restent chez eux pendant les travaux. Cela demande un phasage strict plutôt qu'un chantier ouvert partout à la fois.",
        ],
        list: [
          "Découpage par pièces ou par niveaux, avec une zone de vie toujours propre.",
          "Cloisonnement provisoire et protections sur les circulations.",
          "Maintien d'un point d'eau et d'un WC utilisables autant que possible.",
          "Nettoyage en fin de journée, évacuation régulière des déchets.",
        ],
      },
      {
        h2: "La TVA réduite, un point à ne pas oublier",
        p: [
          "Pour un logement achevé depuis plus de deux ans, les travaux d'amélioration bénéficient d'une TVA à 10 %, et certains travaux d'amélioration énergétique d'une TVA à 5,5 %. Nous appliquons ces taux directement sur le devis, sur la base d'une attestation que vous signez — sans démarche compliquée de votre côté.",
        ],
      },
    ],
    faq: [
      {
        q: "La TVA à 10 % s'applique-t-elle à tous les travaux ?",
        a: "Elle s'applique aux travaux d'amélioration, de transformation et d'entretien d'un logement achevé depuis plus de deux ans, main-d'œuvre et matériaux fournis par l'entreprise. Certains travaux d'économie d'énergie relèvent du taux de 5,5 %.",
      },
      {
        q: "Faut-il vider entièrement la maison ?",
        a: "Non. Nous regroupons le mobilier dans une zone protégée et isolons les espaces poussiéreux. Seule la réfection de l'unique pièce d'eau demande une organisation particulière.",
      },
      {
        q: "Peut-on rénover un pavillon en hiver ?",
        a: "Oui, les travaux intérieurs se réalisent toute l'année. Il faut simplement un chauffage d'appoint pour respecter les températures minimales d'application des enduits et des peintures.",
      },
    ],
  },
  {
    slug: "renovation-salle-de-bains-etapes-duree",
    title: "Rénover une salle de bains : les étapes et la durée réelle | RAMA DÉCO",
    description:
      "Étapes techniques dans l'ordre, durée réelle d'un chantier de salle de bains, rôle de l'étanchéité et erreurs les plus fréquentes. Guide RAMA DÉCO.",
    h1: "Rénover une salle de bains : étapes et durée réelle",
    kicker: "Guide technique",
    date: "2026-04-22",
    readTime: "6 min",
    zone: "ile-de-france",
    img: "https://images.unsplash.com/photo-1564540579594-0930edb6de43?auto=format&fit=crop&w=1600&q=80",
    chapo:
      "C'est la pièce la plus technique d'un logement, et celle où les malfaçons se paient le plus cher. Voici comment se déroule une rénovation de salle de bains menée correctement, et ce que vous devez retrouver dans un devis sérieux.",
    sections: [
      {
        h2: "Les étapes, dans le bon ordre",
        p: ["Une salle de bains se construit du support vers la finition, jamais l'inverse."],
        list: [
          "Dépose complète : sanitaires, faïence, sol, cloisons légères, évacuation.",
          "Réseaux : alimentation, évacuations avec pentes vérifiées, ligne électrique dédiée, ventilation.",
          "Support : doublage ou cloison hydrofuge, chape ou forme de pente pour la douche à l'italienne.",
          "Étanchéité : système sous carrelage sur les zones exposées, bandes d'angle et renforts.",
          "Carrelage et faïence, joints, puis silicone en dernier.",
          "Sanitaires, robinetterie, meuble, sèche-serviettes, appareillage électrique.",
          "Mise en eau, essais, contrôle des pentes, nettoyage de livraison.",
        ],
      },
      {
        h2: "Pourquoi 10 à 15 jours ouvrés, et pas cinq",
        p: [
          "La durée n'est pas liée au temps de travail mais aux séchages. Une chape a besoin de plusieurs jours, une étanchéité de 24 à 48 h, un joint de plusieurs heures avant mise en eau. Un chantier annoncé en cinq jours saute nécessairement une de ces étapes — et cela finit toujours par se voir.",
        ],
      },
      {
        h2: "L'étanchéité : le poste invisible qui compte le plus",
        p: [
          "La faïence n'est pas étanche, le joint non plus. C'est le système d'étanchéité appliqué sous le carrelage qui protège le mur et le plancher. Il ne se voit jamais une fois la pièce terminée, ce qui explique qu'il soit souvent absent des devis les moins chers.",
        ],
        list: [
          "Zones concernées : sol de douche et relevés, murs de la zone d'aspersion, périphérie de baignoire.",
          "Traitement systématique des angles et des passages de canalisation.",
          "Respect du temps de séchage avant pose du carrelage.",
        ],
      },
      {
        h2: "Les cinq erreurs que nous voyons le plus souvent",
        p: ["Ce sont toujours les mêmes, et elles finissent toutes par une reprise coûteuse."],
        list: [
          "Absence de système d'étanchéité sous carrelage.",
          "Ventilation absente ou obstruée : moisissures en moins de deux hivers.",
          "Pente de douche insuffisante : eau stagnante et joints qui noircissent.",
          "Pas de ligne électrique dédiée pour le sèche-serviettes ou le chauffe-eau.",
          "Carrelage posé sur un support non plan : désaffleurs visibles en lumière rasante.",
        ],
      },
    ],
    faq: [
      {
        q: "Peut-on carreler par-dessus l'ancienne faïence ?",
        a: "Techniquement possible sur un support sain, parfaitement adhérent et dégraissé, mais nous le déconseillons : cela empêche de contrôler l'étanchéité et de reprendre les réseaux. Nous ne le proposons qu'avec des réserves écrites.",
      },
      {
        q: "Douche à l'italienne ou receveur extra-plat ?",
        a: "L'italienne demande une forme de pente et donc de la hauteur disponible : idéale en maison ou sur dalle béton. Le receveur extra-plat offre un rendu très proche, une étanchéité plus simple à maîtriser et une mise en œuvre plus rapide : souvent le bon choix en appartement.",
      },
      {
        q: "Peut-on utiliser les WC pendant les travaux ?",
        a: "Oui dans la plupart des cas, y compris quand les WC sont dans la salle de bains : nous organisons le phasage pour maintenir un usage, sauf pendant les deux ou trois jours de réfection du sol.",
      },
    ],
  },
  {
    slug: "peinture-interieure-preparation-finitions",
    title: "Peinture intérieure : la préparation fait le rendu | RAMA DÉCO",
    description:
      "Pourquoi deux chantiers de peinture identiques ne donnent pas le même résultat : préparation des supports, choix des finitions mat, velours ou satin, nombre de couches.",
    h1: "Peinture intérieure : la préparation fait le rendu",
    kicker: "Guide technique",
    date: "2026-04-08",
    readTime: "5 min",
    zone: "ile-de-france",
    img: "https://images.unsplash.com/photo-1503174971373-b1f69850bded?auto=format&fit=crop&w=1600&q=80",
    chapo:
      "Une peinture ne masque pas un défaut, elle le révèle. Sous un éclairage rasant, le moindre rebouchage mal poncé devient visible. Tout se joue avant l'ouverture du premier pot.",
    sections: [
      {
        h2: "La préparation, c'est 60 % du travail",
        p: [
          "C'est la partie que personne ne voit sur les photos, et celle qui distingue un chantier qui tient dix ans d'un chantier qui se marque au premier hiver.",
        ],
        list: [
          "Protection intégrale des sols, du mobilier et des menuiseries.",
          "Lessivage et dépoussiérage des supports.",
          "Rebouchage des trous, traitement des fissures non structurelles.",
          "Enduit de lissage localisé ou complet selon l'état du mur.",
          "Ponçage, puis dépoussiérage à nouveau.",
          "Impression adaptée au support avant les couches de finition.",
        ],
      },
      {
        h2: "Mat, velours ou satin : où utiliser quoi",
        p: ["Le choix d'une finition n'est pas seulement décoratif, il est fonctionnel."],
        list: [
          "Mat : plafonds et chambres. Masque les défauts, mais se lave mal.",
          "Velours : séjours et couloirs. Bon compromis entre lessivabilité et rendu.",
          "Satin : cuisine, salle de bains, boiseries et zones de passage intensif.",
        ],
      },
      {
        h2: "Deux couches, mais sur quel fond ?",
        p: [
          "Sur un support préparé et imprimé, deux couches de finition suffisent. En cas de changement de teinte important — un mur sombre repeint en blanc, par exemple — une impression opacifiante devient indispensable, parfois suivie d'une troisième couche.",
        ],
      },
      {
        h2: "Comparer deux devis peinture, concrètement",
        p: [
          "Demandez trois informations : le nombre de couches de finition, la nature exacte de la préparation prévue, et la référence des produits. Avec ces trois éléments, un écart entre deux devis devient immédiatement compréhensible.",
        ],
        p2: [
          "Dans nos devis, la préparation est toujours décrite séparément de l'application. C'est la seule façon de rendre une comparaison honnête possible.",
        ],
      },
    ],
    faq: [
      {
        q: "Combien de temps pour repeindre un appartement de 60 m² ?",
        a: "Environ 5 à 8 jours ouvrés pour murs et plafonds avec préparation courante, protections et nettoyage inclus. Un support dégradé nécessitant un enduit de lissage complet ajoute 3 à 5 jours.",
      },
      {
        q: "Faut-il vider les pièces avant l'intervention ?",
        a: "Non, mais il faut dégager les murs. Nous regroupons et protégeons le mobilier lourd, démontons les luminaires et remettons tout en place à la fin du chantier.",
      },
      {
        q: "Quelles peintures utilisez-vous ?",
        a: "Des gammes professionnelles adaptées à chaque support et à chaque pièce : lessivable en circulation, spécifique pour les pièces humides. Les références retenues sont écrites dans le devis.",
      },
    ],
  },
  {
    slug: "renovation-appartement-avant-location",
    title: "Remettre un appartement en état avant location | RAMA DÉCO",
    description:
      "Travaux prioritaires, obligations de décence, durées réelles et suivi à distance : le guide des propriétaires bailleurs en Île-de-France par RAMA DÉCO.",
    h1: "Remettre un appartement en état avant location",
    kicker: "Investisseurs & bailleurs",
    date: "2026-03-19",
    readTime: "6 min",
    zone: "seine-saint-denis",
    img: "https://images.unsplash.com/photo-1721742146903-5b37cb82ff54?auto=format&fit=crop&w=1600&q=80",
    chapo:
      "Pour un bailleur, chaque semaine de vacance est un coût. L'objectif d'une remise en état n'est donc pas de tout refaire, mais de cibler les travaux qui améliorent réellement la location — et de tenir le calendrier annoncé.",
    sections: [
      {
        h2: "Les quatre travaux qui changent une location",
        p: [
          "Sur nos chantiers locatifs, quatre postes expliquent l'essentiel de la différence entre un bien qui se loue en une semaine et un bien qui reste vide un mois.",
        ],
        list: [
          "Peinture complète en teinte claire et neutre : effet immédiat sur les visites.",
          "Sol homogène dans tout le logement : le plus fort levier de perception.",
          "Pièce d'eau saine : joints refaits, faïence propre, robinetterie neuve, ventilation opérationnelle.",
          "Éclairage et appareillage électrique remis à neuf : effort limité, impression de logement entretenu.",
        ],
      },
      {
        h2: "Ce qui relève de l'obligation, pas du confort",
        p: [
          "Un logement doit être décent au sens de la réglementation. Certains points ne sont pas négociables et doivent être traités en priorité.",
        ],
        list: [
          "Installation électrique intérieure sans risque : protections, terre, absence de fils nus.",
          "Ventilation effective dans les pièces d'eau et la cuisine.",
          "Absence d'humidité persistante et de moisissures.",
          "Étanchéité correcte des pièces d'eau et évacuations fonctionnelles.",
        ],
      },
      {
        h2: "Les durées à prévoir entre deux locataires",
        p: ["Sur un logement vide, une remise en état complète tient en une à trois semaines."],
        list: [
          "Studio : 5 à 8 jours ouvrés.",
          "T2 : 8 à 12 jours ouvrés.",
          "T3 : 10 à 15 jours ouvrés.",
          "T4 : 15 à 20 jours ouvrés.",
        ],
        p2: [
          "Prévoyez la visite technique avant même le départ du locataire : le chantier peut alors démarrer dès la restitution des clés.",
        ],
      },
      {
        h2: "Piloter un chantier à distance",
        p: [
          "Beaucoup de nos clients bailleurs n'habitent pas la même ville que leur bien. Nous avons construit notre espace projet en ligne pour cela : phases datées, photos d'avancement, devis et factures, messages écrits. Un seul déplacement suffit généralement, à la réception.",
        ],
      },
    ],
    faq: [
      {
        q: "Les travaux de remise en état sont-ils déductibles ?",
        a: "Les dépenses d'entretien, de réparation et d'amélioration d'un logement loué nu sont en principe déductibles des revenus fonciers, contrairement aux travaux de construction ou d'agrandissement. Nos factures sont détaillées par poste pour faciliter le traitement comptable ; votre conseil fiscal reste l'interlocuteur de référence.",
      },
      {
        q: "Proposez-vous des photos avant / après ?",
        a: "Oui, systématiquement. Elles servent à l'annonce de location, à la justification des travaux et à l'état des lieux d'entrée.",
      },
      {
        q: "Travaillez-vous avec les agences de gestion locative ?",
        a: "Oui, régulièrement. Devis détaillé, planning ferme, photos avant/après et facture conforme : le dossier est directement exploitable en gestion.",
      },
    ],
  },
  {
    slug: "second-oeuvre-logement-neuf-checklist",
    title: "Second œuvre en logement neuf : la checklist des finitions | RAMA DÉCO",
    description:
      "Logement neuf livré brut : quels lots de second œuvre prévoir, dans quel ordre, et comment faire lever les réserves de livraison. Guide RAMA DÉCO.",
    h1: "Second œuvre en logement neuf : la checklist des finitions",
    kicker: "Logement neuf",
    date: "2026-02-27",
    readTime: "6 min",
    zone: "ile-de-france",
    img: "https://images.unsplash.com/photo-1680416124510-5eae1beca412?auto=format&fit=crop&w=1600&q=80",
    chapo:
      "Un logement neuf livré n'est pas un logement fini. Entre ce que prévoit la notice du promoteur et ce que vous attendez réellement, il reste presque toujours une série de lots de second œuvre à traiter.",
    sections: [
      {
        h2: "Ce que la notice ne couvre presque jamais",
        p: ["Les postes suivants reviennent sur la quasi-totalité des livraisons que nous reprenons."],
        list: [
          "Peinture de finition soignée : la couche promoteur est souvent une simple impression.",
          "Faïence au-delà de la hauteur minimale contractuelle.",
          "Sols dans les pièces livrées en dalle brute ou en revêtement d'entrée de gamme.",
          "Plinthes, seuils et raccords de finition.",
          "Points lumineux et prises supplémentaires, variateurs, spots encastrés.",
          "Rangements, placards et aménagements intérieurs.",
        ],
      },
      {
        h2: "L'ordre à respecter après la remise des clés",
        p: ["Un logement neuf se termine dans un ordre précis, sous peine de refaire deux fois les finitions."],
        list: [
          "Relevé des réserves et signalement au promoteur dans les délais contractuels.",
          "Modifications électriques et de plomberie intérieures, avant toute finition murale.",
          "Cloisons complémentaires non porteuses, bandes et enduits.",
          "Peinture des plafonds puis des murs.",
          "Faïence et carrelage, puis sols souples et parquets.",
          "Plinthes, portes, appareillage, luminaires et joints de finition.",
        ],
      },
      {
        h2: "Réserves de livraison : ne rien laisser passer",
        p: [
          "Les défauts constatés à la livraison doivent être consignés au procès-verbal. Nous établissons pour nos clients un relevé technique photographique, poste par poste, directement exploitable dans les échanges avec le promoteur. Ce qui n'est pas écrit à la livraison devient très difficile à faire reprendre ensuite.",
        ],
      },
      {
        h2: "Le piège du chantier fait dans le désordre",
        p: [
          "Dans le neuf, la tentation est grande d'installer la cuisine ou de poser le parquet dès la remise des clés, avant les compléments électriques ou la peinture. C'est presque toujours une double dépense : protection, démontage partiel, reprises de raccords. Une semaine d'attente au bon moment économise plusieurs jours de reprise.",
        ],
      },
    ],
    faq: [
      {
        q: "Peut-on faire des travaux juste après la livraison ?",
        a: "Oui, dès la remise des clés, à condition de respecter le règlement de l'immeuble et de ne pas toucher aux éléments couverts par les garanties du constructeur. Nous vérifions ce point avant d'intervenir, pour ne pas fragiliser votre garantie de parfait achèvement.",
      },
      {
        q: "Intervenez-vous en sous-traitance pour un promoteur ?",
        a: "Oui, sur les lots de second œuvre intérieur : plâtrerie, peinture, sols, faïence, menuiseries intérieures et finitions. Nous fournissons décennale, RC pro et attestation de vigilance, et nous travaillons sur planning tous corps d'état.",
      },
      {
        q: "Réalisez-vous les finitions d'une VEFA en cours de construction ?",
        a: "Nous intervenons après livraison. Avant livraison, les travaux modificatifs acquéreur passent obligatoirement par le promoteur et ses entreprises.",
      },
    ],
  },
];

export const guideBySlug = (slug) => GUIDES.find((g) => g.slug === slug);
export const guideUrl = (slug) => `/blog/${slug}`;
